import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { products } from '../data/products';
import ProductCard from '../components/ProductCard';
import { Leaf, HeartHandshake, Sparkles, Truck, ArrowRight } from 'lucide-react';
import FeaturedHerb from '../components/FeaturedHerb';
import FounderNote from '../components/FounderNote';

const Home = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const featured = products.slice(0, 8);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/hero-herbs-flatlay.jpg"
            alt="Herbs We Love"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/25 to-black/50" />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 min-h-[80vh] md:min-h-[88vh] flex flex-col justify-center items-center text-center text-[#faf7f1]">
          <div className="font-script text-2xl md:text-3xl text-[#dcd6c7] mb-4 reveal">Herbs. Healing. Heart.</div>
          <h1 className="font-serif-elegant text-5xl md:text-7xl lg:text-8xl leading-tight tracking-tight max-w-4xl reveal" style={{ animationDelay: '120ms' }}>
            Where Food Becomes Medicine.
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg text-[#f0ead9] leading-relaxed reveal" style={{ animationDelay: '240ms' }}>
            Start your healing journey with our range of CBD and herbal products today.
          </p>
          <Link to="/collections/all" className="mt-10 btn-outline !border-[#faf7f1] !text-[#faf7f1] hover:!bg-[#faf7f1] hover:!text-[#3a4a3b] reveal" style={{ animationDelay: '360ms' }}>
            Shop Now
          </Link>
        </div>
      </section>

      {/* Values bar */}
      <section className="bg-[#efe9db] border-y border-[#e5decf]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { icon: Leaf, title: 'All Natural', text: 'Ethically sourced botanicals' },
            { icon: HeartHandshake, title: 'Handcrafted', text: 'Made in small batches with love' },
            { icon: Sparkles, title: 'Holistic', text: 'Body, mind & spirit wellness' },
            { icon: Truck, title: 'Fast Shipping', text: 'Standard 3-7 days, Express 1-3' },
          ].map(({ icon: Icon, title, text }) => (
            <div key={title} className="flex flex-col items-center text-center">
              <Icon className="w-7 h-7 text-[#3a4a3b] mb-3" />
              <div className="font-serif-elegant text-lg mb-1">{title}</div>
              <div className="text-xs text-[#6f6a5f] uppercase tracking-widest">{text}</div>
            </div>
          ))}
        </div>
      </section>

      {/* We Truly Care */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-2 gap-14 items-center">
        <div className="order-2 md:order-1">
          <div className="uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-4">Our approach</div>
          <h2 className="section-title text-4xl md:text-5xl leading-tight mb-6">We Truly Care About Your Health</h2>
          <p className="text-[#4a4640] leading-relaxed mb-6">
            We take a holistic approach to preventing and reversing disease through natural wellness solutions. Our range of holistic products, including CBD and various herbal products, focuses on treating you as a whole, not just your condition.
          </p>
          <p className="text-[#4a4640] leading-relaxed mb-8">
            We believe that you cannot live your best life possible unless your mind and body are completely healthy.
          </p>
          <Link to="/about" className="btn-outline">Learn More</Link>
        </div>
        <div className="order-1 md:order-2 relative">
          <div className="absolute -inset-4 bg-[#cfd8c2]/40 -z-10" />
          <div className="w-full bg-[#faf7f1] p-2 shadow-lg">
            <img
              src="/images/we-truly-care.webp"
              alt="Handcrafted herbal remedies at Herbs We Love"
              className="w-full h-auto object-contain block"
            />
          </div>
        </div>
      </section>

      {/* Featured Herb of the Week */}
      <FeaturedHerb />

      {/* Founder Note */}
      <FounderNote />

      {/* Ritual Quiz CTA */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
        <div className="bg-[#3a4a3b] text-[#f6f2ea] p-10 md:p-16 grid md:grid-cols-[1fr_auto] gap-6 items-center">
          <div>
            <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#dcd6c7] mb-3">
              <Sparkles className="w-4 h-4" /> Ritual Quiz
            </div>
            <h2 className="font-serif-elegant text-3xl md:text-5xl mb-3">Not sure where to start?</h2>
            <p className="text-[#e6e0d0] leading-relaxed max-w-xl">Take our 60-second Ritual Quiz and we&apos;ll match you with the herbs your body is asking for.</p>
          </div>
          <Link to="/quiz" className="btn-outline !border-[#dcd6c7] !text-[#dcd6c7] hover:!bg-[#dcd6c7] hover:!text-[#3a4a3b] inline-flex items-center gap-2 whitespace-nowrap">
            Take the Quiz <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Featured Products */}
      <section className="bg-[#faf7f1] py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <div className="uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-3">Best sellers</div>
            <h2 className="section-title text-4xl md:text-5xl">Beloved Botanicals</h2>
            <p className="mt-4 text-[#4a4640] max-w-xl mx-auto">Our most-loved elixirs, skincare and sacred blends &mdash; each one hand-poured with intention.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
            {featured.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
          <div className="text-center mt-14">
            <Link to="/collections/all" className="btn-primary">View All Products</Link>
          </div>
        </div>
      </section>

      {/* A Legacy of Natural Care */}
      <section className="bg-[#3a4a3b] text-[#f6f2ea] mt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="bg-[#faf7f1] p-2 shadow-lg">
              <img
                src="/images/herbalist-team.webp"
                alt="The Herbs We Love team in the apothecary"
                className="w-full h-auto object-contain block"
              />
            </div>
          </div>
          <div>
            <div className="uppercase tracking-[0.35em] text-xs text-[#dcd6c7] mb-4">Our story</div>
            <h2 className="font-serif-elegant text-4xl md:text-5xl mb-6">A Legacy of Natural Care</h2>
            <p className="text-[#e6e0d0] leading-relaxed mb-6">
              For over 20 years, our trusted herbal partners at Willow Creek Springs have been dedicated to creating all-natural skincare and wellness products. We specialize in holistic health by offering a range of herbal products that include moisturizers, pain relief solutions, anti-aging treatments, and CBD formulas.
            </p>
            <p className="text-[#e6e0d0] leading-relaxed mb-8">
              Our powerful ingredients &mdash; turmeric, cayenne, ginger, rosemary, and primrose oil &mdash; are chosen for their proven benefits.
            </p>
            <Link to="/collections/all" className="btn-outline !border-[#dcd6c7] !text-[#dcd6c7] hover:!bg-[#dcd6c7] hover:!text-[#3a4a3b]">Explore Products</Link>
          </div>
        </div>
      </section>

      {/* What Makes Us Special */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-2 gap-14 items-center">
        <div className="relative">
          <div className="absolute -inset-4 bg-[#e9d8c4]/50 -z-10" />
          <div className="w-full bg-[#faf7f1] p-2 shadow-lg">
            <img
              src="/images/herbs-flatlay.webp"
              alt="Herbs, oils and flowers laid out on a stone surface"
              className="w-full h-auto object-contain block"
            />
          </div>
        </div>
        <div>
          <div className="uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-4">Why us</div>
          <h2 className="section-title text-4xl md:text-5xl mb-6">What Makes Us Special?</h2>
          <p className="text-[#4a4640] leading-relaxed mb-6">
            We offer handcrafted, herbal products infused with natural ingredients, made in partnership with trusted artisans &mdash; each one designed to support your body, mind, and spirit through holistic health.
          </p>
          <p className="text-[#4a4640] leading-relaxed mb-6">
            From CBD wellness blends to skin-loving botanicals, everything we provide is rooted in the principles of healing and harmony.
          </p>
          <p className="font-script text-2xl text-[#7a8b6b]">With love, The Herbs We Love Team</p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-[#efe9db]">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-24 text-center">
          <div className="uppercase tracking-[0.35em] text-xs text-[#7a8b6b] mb-3">Kind words</div>
          <h2 className="section-title text-4xl md:text-5xl mb-14">Loved by our community</h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              { q: 'The Sleep Syrup has been a total game changer. I finally sleep through the night and wake up refreshed.', a: 'Melissa T.' },
              { q: 'I use the Fireball Elixir at the first sign of a cold. It really works and tastes divine.', a: 'Jordan R.' },
              { q: 'The lip balms are the softest, dreamiest things. I keep one in every bag.', a: 'Ana P.' },
            ].map((t, i) => (
              <div key={i} className="bg-[#faf7f1] p-8 shadow-sm">
                <div className="font-serif-elegant text-2xl mb-4">&ldquo;</div>
                <p className="text-[#4a4640] italic leading-relaxed mb-6">{t.q}</p>
                <div className="text-xs uppercase tracking-widest text-[#7a8b6b]">&mdash; {t.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
